import { ofetch } from "ofetch";
import AppError from "@/util/AppError";
import { defaultWindow } from "@vueuse/core";
import { stringify } from "qs";
import { hasInjectionContext, ref } from "vue";
import localforage from "localforage";
import { Base64 } from "js-base64";

/**
 * @returns {import("vue").Plugin}
 */
export function createOfetch(i18n) {
  const messageStore = useMessageStore();
  const authStore = useAuthStore();
  const _ofetch = ofetch.create({
    baseURL:
      localStorage.getItem("baseUrl") ||
      import.meta.env.VITE_VUE_APP_API_BASE_PATH ||
      (window.cordova?.platformId ? "https://go.preprod.alvarum.com/" : window.location.origin + "/"),
    retryDelay: 600,
    retry: 5,
    retryStatusCodes: [408, 409, 425, 500, 502, 503, 504],
    credentials: "same-origin",
    async onRequest(conf) {
      conf.options.headers.set(
        "X-App-Version",
        import.meta.env.VITE_VUE_APP_RELEASE || "alvarum-go-" + (window.cordova?.platformId || "web") + "@dev"
      );
      if (window.device) {
        conf.options.headers.set("X-Device", `${window.device.version}:${window.device.manufacturer} ${window.device.model}`);
      }

      if (authStore.auth) {
        conf.options.headers.set("Authorization", `Bearer ${authStore.auth.access_token}`);
      }

      if (conf.options.query) {
        const queryString = stringify(conf.options.query);
        if (queryString) {
          conf.request += (conf.request.toString().indexOf("?") === -1 ? "?" : "&") + queryString;
        }
        delete conf.options.query;
      }
    },
    async onRequestError({ error, options }) {
      if (!options.retry) {
        messageStore.error(i18n.global.t("errors.server.notreachable"));
        return Promise.reject(error);
      }
    },
    async onResponseError({ response, options }) {
      const status = response.status;
      const errorData = response._data.error || response._data;
      if (status === 401) {
        if (console && console.log) console.log("401 Unauthorized", errorData);
        useBrowserLocation().value.href = "/logout";
      } else if (status === 400) {
        if (console && console.log) console.log("Bad request", errorData);
        if (errorData.name === "AppError") {
          messageStore.error(i18n.global.t(errorData.message, errorData.params));
        } else if (errorData.name !== "FormError") {
          messageStore.error(i18n.global.t("errors.server.validation"));
        }
      } else if (status === 500) {
        if (console && console.log) console.log("Technical error", errorData);
        messageStore.error(i18n.global.t("errors.server.technical"));
      } else if (status === 404) {
        if (console && console.log) console.log("Not found error", errorData);
        messageStore.error(i18n.global.t("errors.server.notFound"));
      } else if (status === 429) {
        if (console && console.log) console.log("Rate limited", errorData);
        messageStore.error(i18n.global.t("errors.server.tooManyRequests"));
      } else if (options.retry && options.retryStatusCodes?.includes(status)) {
        // retry request
        return;
      } else {
        if (console && console.log) console.log("Unexpected error", status, errorData);
        messageStore.error(i18n.global.t("errors.server.unexpected"));
      }
      let returnedError = errorData;
      if (returnedError && !(returnedError instanceof Error)) {
        if (returnedError.name === "AppError") {
          returnedError = new AppError(returnedError.message, returnedError.params);
        }
      }
      throw returnedError;
    },
  });
  if (!import.meta.env.SSR) {
    defaultWindow["ofetch"] = _ofetch;
  }
  _ofetch["install"] = function (app) {
    app.provide("ofetch", this);
  };
  // @ts-ignore
  return _ofetch;
}

/**
 * @param {(args: any[]) => Parameters<import("ofetch").ofetch>} optionsGenerator
 * @returns {FetchAPI<any>}
 */
export function createAPIHandler(optionsGenerator) {
  const _fetch = async (args, cache) => {
    const [url, options] = optionsGenerator(args);
    const { method, body, query } = options;

    if (method === "GET" && (navigator.onLine === false || cache)) {
      const key = getCacheKey(url, query);
      const cachedResponse = await cacheStorage.getItem(key);
      if (cachedResponse) {
        if (cache) {
          const backgroundRefresh = async () => {
            try {
              const ofetch = (hasInjectionContext() && inject("ofetch")) || defaultWindow["ofetch"];
              const freshResponse = await ofetch(url, { method, body, query });
              if (freshResponse) {
                cacheResponse(url, query, freshResponse, key);
                if (typeof cache === "function") {
                  cache(freshResponse);
                }
              }
            } catch {
              // Silently ignore background refresh errors; the cached value remains valid.
            }
          };

          if (navigator.onLine) {
            setTimeout(backgroundRefresh, 0);
          }
        }
        return cachedResponse;
      }
    }

    const ofetch = (hasInjectionContext() && inject("ofetch")) || defaultWindow["ofetch"];
    const response = await ofetch(url, { method, body, query });

    if (method === "GET" && cache !== false && response) {
      cacheResponse(url, query, response);
    }

    return response;
  };

  // @ts-ignore
  return Object.assign(
    function (...args) {
      return _fetch(args, this._cache);
    },
    {
      ref: function (defaultValue) {
        const _cache = this._cache;
        const _ref = Object.assign(ref(defaultValue), {
          _args: null,
          fetch(...args) {
            this._args = args;
            let _onFulFilled, _onRejection, _onFinally;
            // @ts-ignore
            _fetch(args, _cache)
              .then((result) => {
                _ref.value = result;
                if (_onFulFilled) _onFulFilled(result);
              })
              .catch((error) => {
                if (_onRejection) _onRejection(error);
              })
              .finally(() => {
                if (_onFinally) _onFinally();
              });
            return Object.assign(_ref, {
              then: (onFulFilled, onRejection) => {
                _onFulFilled = onFulFilled;
                if (onRejection) _onRejection = onRejection;
                return _ref;
              },
              finally: (onFinally) => {
                _onFinally = onFinally;
                return _ref;
              },
              catch: (onRejection) => {
                _onRejection = onRejection;
                return _ref;
              },
            });
          },
          resetCache: async function () {
            // Clear the cache for this specific API call
            if (!this._args) return;
            const [url, options] = optionsGenerator(this._args);
            const key = getCacheKey(url, options.query);
            await cacheStorage.removeItem(key);
          },
        });
        return _ref;
      },
      refs: function (defaultValue) {
        const _cache = this._cache;
        const _refs = Object.assign(Object.fromEntries(Object.entries(defaultValue).map(([k, v]) => [k, ref(v)])), {
          fetch(...args) {
            let _onFulFilled, _onRejection, _onFinally;
            // @ts-ignore
            _fetch(args, _cache)
              .then((result) => {
                for (const key in result) {
                  if (_refs[key]) _refs[key].value = result[key];
                }
                if (_onFulFilled) _onFulFilled(result);
              })
              .catch((error) => {
                if (_onRejection) _onRejection(error);
              })
              .finally(() => {
                if (_onFinally) _onFinally();
              });
            return Object.assign(_refs, {
              then: (onFulFilled, onRejection) => {
                _onFulFilled = onFulFilled;
                if (onRejection) _onRejection = onRejection;
                return _refs;
              },
              finally: (onFinally) => {
                _onFinally = onFinally;
                return _refs;
              },
              catch: (onRejection) => {
                _onRejection = onRejection;
                return _refs;
              },
            });
          },
        });
        return _refs;
      },
      _cache: null,
      cache: function (callback = null) {
        this._cache = callback === false ? false : callback || true;
        return this;
      },
      fetch: function (...args) {
        return _fetch(args, this._cache);
      },
    }
  );
}

/**
 *
 * @type {typeof ofetch.raw<any,any>}
 */
export const useOFetchRaw = async function (url, options) {
  return await ((hasInjectionContext() && inject("ofetch")) || defaultWindow["ofetch"]).raw(url, options);
};

export const cacheStorage = localforage.createInstance({
  name: "ofetch",
});
cacheStorage.setDriver(localforage.LOCALSTORAGE);

export const cacheResponse = async (url, query, response, key = null) => {
  key = key || getCacheKey(url, query);
  let responseSize = JSON.stringify(response)?.length;
  if (responseSize > 100000) {
    console.error("Response too large to cache:", url, query, responseSize / 1000, "kB");
    await cacheStorage.removeItem(key, response);
    return false;
  }
  try {
    await cacheStorage.setItem(key, response);
  } catch (error) {
    console.error("Failed to cache response:", url, query, error);
    await clearCache();
  }
};
export const getCacheKey = (url, params) => Base64.encodeURI(url + "?" + JSON.stringify(params));
export const clearCache = async () => await cacheStorage.clear();
